import type { JSX } from 'react';
import { Box, Link, Popover, TableCell, TableHeader, Text } from '@ksyos/design-system';
import { useTheme } from 'styled-components';
import type { IOutclinicDashboardProcess } from '@common/interfaces/api/outclinicDashboardApi';
import { getDisplayName } from '@common/utils/getDisplayName';
import { getSelectedLanguage } from '@frontend/translate';
import { OutclinicDashboardSnooze } from '../outclinicDashboardSnooze/OutclinicDashboardSnooze';

type OutclinicDashboardTableRowHeaderProps = Readonly<{
    isColumnHeader?: boolean;
    label?: string;
    patient?: IOutclinicDashboardProcess['patient'];
    processId?: string;
    processNumber?: number;
    setSnoozeStatus?: (processId: string, isSnoozed: boolean, snoozedUntilDateTime?: string) => void;
    snoozedUntilDateTime?: string;
    url?: string;
}>;

export function OutclinicDashboardTableRowHeader(props: OutclinicDashboardTableRowHeaderProps): JSX.Element {
    const theme = useTheme();

    const stickyStyles = {
        boxShadow: theme.shadows.md,
        // Ensures that the box shadow is only visible on the right
        clipPath: 'inset(0 -15px 0 0)',
        fontWeight: theme.fontWeights.medium,
        left: 0,
        position: 'sticky',
        pr: props.isColumnHeader ? 0 : 8,
        textAlign: 'center',
        width: 100,
        zIndex: 1,
    };

    // The first cell of the first row
    if (props.isColumnHeader) {
        const headerBorderBottom = `1px solid ${theme.colors.neutral300}`;

        return (
            <TableHeader
                sx={{
                    ...stickyStyles,
                    borderBottom: props.label ? headerBorderBottom : undefined,
                }}
            >
                <Box pl={2}>{props.label}</Box>
            </TableHeader>
        );
    }

    // Consecutive rows, start with the PID cell
    return (
        <TableCell
            sx={{
                ...stickyStyles,
                backgroundColor: props.snoozedUntilDateTime ? theme.colors.neutral100 : undefined,
            }}
        >
            {props.patient && props.processNumber && props.processId && props.setSnoozeStatus && (
                <Box
                    alignItems="center"
                    display="flex"
                    justifyContent="start"
                >
                    <OutclinicDashboardSnooze
                        processId={props.processId}
                        processNumber={props.processNumber}
                        setSnoozeStatus={props.setSnoozeStatus}
                        snoozedUntilDateTime={props.snoozedUntilDateTime}
                    />

                    <Popover
                        content={
                            <>
                                <Text size="sm">
                                    {getDisplayName(props.patient)}
                                    {props.patient.gender &&
                                        ` (${getSelectedLanguage().genderEnum[props.patient.gender][0]})`}
                                </Text>
                                {props.url && (
                                    <Link
                                        mt={2}
                                        to={props.url}
                                        variant="link"
                                    >
                                        Ga naar PID
                                    </Link>
                                )}
                            </>
                        }
                        sx={{ boxShadow: theme.shadows.md, width: 'auto', zIndex: 1 }}
                    >
                        <Text
                            as="span"
                            color="primary"
                            fontWeight="medium"
                            size="sm"
                        >
                            {props.processNumber}
                        </Text>
                    </Popover>
                </Box>
            )}
        </TableCell>
    );
}
